import bridge from "@xpeng/js-bridge";
import request from "./request";
import UnionAxios from "../utils/union-axios";
import { getBrowserEnv } from "../utils/tools";
import { getParamsFromHref } from "../utils/url";
import { isHostLoggedOut, resolveUid } from "../utils/host-login-state";

/**
 * 业务请求测试
 */
export const postCancel = () => {
  return request(`/web-h5/charge/conversion/cancel`, {}, "POST");
};
/**
 * 查询VIP卡券获得记录
 */
export function GetQueryMyGoodsRecord() {
  return request(
    `/web-h5/charge/member/queryMyGoodsRecord`,
    { uid: "" },
    "POST",
  );
}
/**
 * 免登录场景（如专题页 guest 访问）标志位：由 ConfigPageProvider 在 isGuest 状态变化时同步写入。
 * 为 true 时，getUserInfo 请求联登 SDK 会跳过"未登录自动跳转登录"的兜底逻辑，
 * 避免免登录页面里的埋点上报、组件曝光等静默取 uid 场景被强制拉去登录。
 */
let disableAutoLogin = false;
export const setDisableAutoLogin = (value: boolean) => {
  disableAutoLogin = value;
};

/**
 * 获取用户信息
 * @param options.disableLogin 显式指定是否关闭联登 SDK 的"未登录自动跳转登录"兜底逻辑；
 * 不传时回退到模块级 disableAutoLogin 标志位
 */
export const getUserInfo = (options?: { disableLogin?: boolean }) => {
  const disableLogin = options?.disableLogin ?? disableAutoLogin;
  return UnionAxios.get(
    `/web-h5/charge/member/getCurrentUserInfo`,
    {},
    disableLogin ? { disableLogin: true } : {},
  );
};

/**
 * 获取当前用户积分余额
 */
export function GetUserCredit() {
  return request(`/web-h5/charge/getUserCredit`, {}, "GET");
}

function getCookie(name) {
  const cookieString = document.cookie;
  const cookies = cookieString.split("; ");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].split("=");
    if (cookie[0] === name) {
      return decodeURIComponent(cookie[1]);
    }
  }
  return null;
}
/**
 * uid 有效性校验：联登 SDK 在 APP 未登录时会把 preUid 置为 "0" 并写进 sessionStorage
 * （见 @xpeng/union-login 的 getLoginState），"0" 不是真实 uid，不能当成已登录；
 * "-1" 是小程序退出登录后写入本地的占位值，同样不是真实 uid
 */
export const isValidUid = (uid?: string | number | null): boolean => {
  const value = String(uid ?? "").trim();
  return value !== "" && value !== "0" && value !== "-1";
};

export const getCachedUid = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  // 宿主（小程序）声明的身份优先于任何本地缓存：
  //   hostLoginState=0 —— webview 内的 sessionUid / cookie 都是退登前的残留，一律不可信；
  //   hostLoginState=1 —— 以 URL 上的 uid 为准，避免切号后读到 cookie 里的上一个 uid。
  // 无宿主声明（APP / 外部浏览器）时走 fallback，沿用原有缓存链。
  return resolveUid(() => {
    const cachedUid =
      sessionStorage.getItem("sessionUid") ||
      getCookie("xp-uid") ||
      getCookie("wasxuid_r") ||
      "";
    return isValidUid(cachedUid) ? cachedUid : "";
  });
};

/**
 * 本次页面生命周期内是否已确认过"取不到 uid"。
 * 用内存态而非 sessionStorage：跳转登录返回后同 tab 的 sessionStorage 不会重置，
 * 会把已登录用户再次误判为未登录。
 */
let probedNotLoggedIn = false;
/** checkLoginUid 的进行中请求，用于并发去重 */
let checkLoginUidPromise: Promise<string> | null = null;

/** JSBridge 查询登录态的超时时间：bridge 未初始化时 callHandler 会一直挂起 */
const APP_LOGIN_STATE_TIMEOUT = 1500;
/** 接口探测登录态的超时时间 */
const REQUEST_LOGIN_STATE_TIMEOUT = 5000;

type ProbeResult = {
  uid: string;
  /** 结论是否可信：超时/异常时为 false，此时不缓存"未登录"，允许后续重试 */
  conclusive: boolean;
};

const withTimeout = (
  task: Promise<string>,
  timeout: number,
): Promise<ProbeResult> => {
  return new Promise<ProbeResult>((resolve) => {
    let settled = false;
    const finish = (result: ProbeResult) => {
      if (!settled) {
        settled = true;
        resolve(result);
      }
    };
    const timer = setTimeout(
      () => finish({ uid: "", conclusive: false }),
      timeout,
    );
    task
      .then((uid) => finish({ uid, conclusive: true }))
      .catch(() => finish({ uid: "", conclusive: false }))
      .finally(() => clearTimeout(timer));
  });
};

/**
 * APP 内直接向原生查询登录态：不发网络请求、不触发联登跳转。
 * APP 内的登录态由原生持有（联登 SDK 也是靠 getLoginState 拿 preUid 防串号），
 * 而 getCurrentUserInfo 在联登会话建立前必然返回未登录，不能用它判断 APP 登录态。
 */
const getAppLoginUid = async (): Promise<string> => {
  const res = await bridge.callHandler("getLoginState", {});
  let data = res?.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return "";
    }
  }
  if (Number(res?.loginState) !== 1) {
    return "";
  }
  const uid = String(data?.uid ?? "");
  return isValidUid(uid) ? uid : "";
};

/** 接口探测登录态：显式关闭自动跳转，仅用于判断，不拉起登录 */
const requestLoginUid = async (): Promise<string> => {
  const res = await getUserInfo({ disableLogin: true });
  if (res?.code !== 200) {
    return "";
  }
  const uid = String(res?.data?.uid ?? "");
  return isValidUid(uid) ? uid : "";
};

const probeLoginUid = (): Promise<ProbeResult> => {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  if (isXPApp) {
    return withTimeout(getAppLoginUid(), APP_LOGIN_STATE_TIMEOUT);
  }
  if (isWXApp || isMyApp) {
    // 小程序 webview 的 uid 由宿主拼在 URL 上，联登 SDK 防串号也是读这里
    const urlUid = getParamsFromHref("uid");
    if (isValidUid(urlUid)) {
      return Promise.resolve({ uid: urlUid, conclusive: true });
    }
  }
  return withTimeout(requestLoginUid(), REQUEST_LOGIN_STATE_TIMEOUT);
};

/**
 * 登录态探测：仅用于判断用户是否已登录，任何情况下都不会跳转登录页、不会拉起联登。
 *
 * APP/小程序 webview 内 sessionUid 是联登 SDK 在发起首个请求时由 preUid 写入的，
 * 首屏渲染那一刻 getCachedUid() 必然为空，因此不能用它判断初始登录态；
 * 而 APP 内 getCurrentUserInfo 在联登会话建立前必然返回未登录，也不能用它的返回码判断。
 * 因此按环境分流：APP 问 JSBridge，小程序读 URL uid，其余环境用接口 + disableLogin 探测。
 * 各分支都有超时兜底，避免页面把渲染时机压在一个永不 settle 的 Promise 上。
 *
 * @param options.force 忽略"已确认未登录"的短路，重新发起真实探测
 */
export const checkLoginUid = (options?: { force?: boolean }) => {
  // 宿主（小程序）已声明未登录：直接判未登录，且绝不做接口探测。
  // 小程序退登不会作废 webview 侧的联登会话，一旦问后端就会拿回旧 uid 并被写回缓存
  if (isHostLoggedOut()) {
    return Promise.resolve("");
  }
  const cachedUid = getCachedUid();
  if (cachedUid) {
    return Promise.resolve(cachedUid);
  }
  if (options?.force) {
    probedNotLoggedIn = false;
  }
  if (probedNotLoggedIn) {
    return Promise.resolve("");
  }
  if (checkLoginUidPromise) {
    return checkLoginUidPromise;
  }
  checkLoginUidPromise = probeLoginUid()
    .then(({ uid, conclusive }) => {
      if (uid) {
        // 与联登 SDK 写 preUid 的行为保持一致，后续请求的 XP-Uid 也就有值了
        sessionStorage.setItem("sessionUid", uid);
        probedNotLoggedIn = false;
      } else if (conclusive) {
        probedNotLoggedIn = true;
      }
      return uid;
    })
    .catch(() => "")
    .finally(() => {
      checkLoginUidPromise = null;
    });
  return checkLoginUidPromise;
};

/**
 * uid获取通用方法
 * 在普通浏览器端static域名拿不到chargegateway域名的cookie，即无法获取到uid
 * 这里做一个特殊处理，若在APP/小程序环境内，拿联登sdk处理时传入的preUid作为当前用户uid，在普通浏览器内使用接口获取uid
 */
export const getCurrentUid = () => {
  return new Promise<string>((resolve) => {
    // 宿主已声明未登录：不去问后端，否则仍有效的旧会话会把已登出用户判回已登录
    if (isHostLoggedOut()) {
      resolve("");
      return;
    }
    const sessionUid = getCachedUid();
    if (sessionUid) {
      resolve(sessionUid);
      return;
    }
    // guest 模式下，若本次会话已确认过取不到 uid，直接返回空，不重复发起真实请求
    if (disableAutoLogin && probedNotLoggedIn) {
      resolve("");
      return;
    }
    getUserInfo()
      .then((res) => {
        if (res.code === 200 && isValidUid(res?.data?.uid)) {
          const uid = String(res.data.uid);
          sessionStorage.setItem("sessionUid", uid);
          probedNotLoggedIn = false;
          resolve(uid);
        } else {
          if (disableAutoLogin) {
            probedNotLoggedIn = true;
          }
          resolve("");
        }
      })
      .catch(() => {
        resolve("");
      });
  });
};
